// src/context/TutorialContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import AuthContext from './AuthContext';
import { markTutorialSeen as apiMarkTutorialSeen } from '../services/authApi';

const TutorialContext = createContext(null);

// Global kill switch for the guided tour (intro modal + all tutorial tooltips).
// While false, tutorial_seen is never persisted, so new users still get the tour once re-enabled.
export const TUTORIAL_ENABLED = false;

export const TutorialProvider = ({ children, initialStep = 1 }) => {
  const authContext = useContext(AuthContext);
  const tutorialSeen = authContext?.user?.tutorial_seen === true;

  const [step, setStep] = useState(initialStep);
  // Tooltips are inactive until the user opts in via the intro modal.
  const [active, setActive] = useState(false);
  // Only surface the intro modal for users whose tutorial_seen flag is false —
  // i.e. brand-new accounts. Cached user (hydrated from localStorage) supplies
  // this synchronously on mount; the effect below covers the case where the
  // /auth/me response settles after mount and reveals the user has already
  // seen it on another device.
  const [introOpen, setIntroOpen] = useState(() => TUTORIAL_ENABLED && !tutorialSeen);
  const [sampleProjectName, setSampleProjectName] = useState(null);
  const [pendingSampleAsin, setPendingSampleAsin] = useState(null);

  useEffect(() => {
    if (tutorialSeen) setIntroOpen(false);
  }, [tutorialSeen]);

  const persistTutorialSeen = () => {
    if (authContext?.user && !authContext.user.tutorial_seen) {
      // Optimistic local update so subsequent mounts within this session
      // immediately reflect the new state without waiting on the API.
      authContext.updateUser({
        ...authContext.user,
        tutorial_seen: true,
      });
    }
    const token = typeof window !== 'undefined'
      ? window.localStorage.getItem('authToken')
      : null;
    if (token) {
      // Fire-and-forget — UI shouldn't block on the network for this.
      apiMarkTutorialSeen(token).catch((err) => {
        console.error('Failed to persist tutorial_seen:', err);
      });
    }
  };

  const value = useMemo(
    () => ({
      step,
      setStep,
      active: TUTORIAL_ENABLED && active,
      dismiss: () => {
        // Skipping the tour mid-flow counts as "done" — mark it on the user
        // record so the intro modal never resurfaces on a future session,
        // even if startTutorial's earlier persist call failed to land.
        persistTutorialSeen();
        setActive(false);
      },
      reset: () => {
        setStep(1);
        setActive(true);
        setIntroOpen(false);
        setSampleProjectName(null);
        setPendingSampleAsin(null);
      },
      introOpen,
      startTutorial: () => {
        persistTutorialSeen();
        setIntroOpen(false);
        setStep(1);
        setActive(true);
      },
      dismissIntro: () => {
        persistTutorialSeen();
        setIntroOpen(false);
        setActive(false);
      },
      sampleProjectName,
      setSampleProjectName,
      pendingSampleAsin,
      setPendingSampleAsin,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, active, introOpen, sampleProjectName, pendingSampleAsin, authContext?.user]
  );

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export const useTutorial = () => {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    return {
      step: 0,
      setStep: () => {},
      active: false,
      dismiss: () => {},
      reset: () => {},
      introOpen: false,
      startTutorial: () => {},
      dismissIntro: () => {},
      sampleProjectName: null,
      setSampleProjectName: () => {},
      pendingSampleAsin: null,
      setPendingSampleAsin: () => {},
    };
  }
  return ctx;
};

export default TutorialContext;
